// PostHog is lazy-loaded: the ~200 KB `posthog-js` library is pulled in via a
// dynamic import inside initPostHog() (which runs in a mount effect), so it lands
// in its own async chunk instead of the initial bundle every page downloads.
type PostHog = import('posthog-js').PostHog;

// Public client-side project key (safe to ship in the bundle, like a GA id).
const POSTHOG_KEY = 'phc_y8hd8FFnBTxPn8CaFDaQUy6KXzo8MDdmKnzFhmqWXjAm';
const POSTHOG_HOST = 'https://us.i.posthog.com';
const CONSENT_KEY = 'cookie-consent-v1';

let instance: PostHog | null = null;
let initialized = false;

function consentGranted(): boolean {
  try {
    return localStorage.getItem(CONSENT_KEY) === 'accepted';
  } catch {
    return false;
  }
}

/**
 * Initialize PostHog on the client, OPTED OUT by default. Capturing only starts
 * once the visitor accepts cookies (mirrors the GA consent gate). Safe during
 * SSG — no-ops when there is no window. `posthog-js` is imported dynamically so
 * it never enters the synchronous first-paint bundle.
 */
export async function initPostHog() {
  if (initialized || typeof window === 'undefined') return;
  initialized = true; // set before await to guard against a double init race
  const { default: posthog } = await import('posthog-js');
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    person_profiles: 'identified_only',
    autocapture: false,
    capture_pageview: 'history_change',
    capture_pageleave: true,
    opt_out_capturing_by_default: true,
    opt_out_persistence_by_default: true,
    loaded: (ph: PostHog) => {
      instance = ph;
      if (consentGranted()) ph.opt_in_capturing();
    },
  });
  instance = posthog;
}

/** Begin capturing after the visitor accepts cookies (called from the banner). */
export function phOptIn() {
  instance?.opt_in_capturing();
}

/** Capture an event. No-op until PostHog is loaded + consented. */
export function phCapture(name: string, props?: Record<string, unknown>) {
  instance?.capture(name, props);
}

/** Register super-properties that ride on every subsequent event. */
export function phRegister(props: Record<string, unknown>) {
  instance?.register(props);
}
